import React, { useCallback, useEffect, useMemo } from 'react'

import { Col, Hr, Spacer } from '@amzn/stencil-react-components/layout'

import { ItemEditorBasicInfo } from 'src/components/ItemEditorBasicInfo'
import { ItemEntityField } from 'src/components/ItemEntityField'
import { MediaManager } from 'src/components/MediaContentsEditor/MediaManager'
import { ResponsiveRow } from 'src/components/ResponsiveRow'
import { Change } from 'src/hooks/DTOEditor'
import { ContentItemDTO } from 'src/models/dto/items/ContentItemDTO'
import { Medias } from 'src/models/dto/items/MediaDTO'
import { ModuleLayout } from 'src/models/dto/ModuleDTO'
import { ItemEditorProps } from 'src/pages/module-builder/item-editors/ItemEditorContainer'
import {
    ItemEditorTextArea,
    ItemEditorTextInput,
} from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import { ItemEditorValidationErrors } from 'src/pages/module-builder/item-editors/ItemEditorValidationErrors'
import { ItemEntityService } from 'src/services/EntityServices/ItemEntityService'
import { ContentHandler } from 'src/services/EntityServices/ItemUpdateHandlers/ContentHandler'

export const ContentItemEditor = ({
    itemId,
    locale,
    workflowEntityId,
    showValidationError,
    moduleLayout,
    editDisabled,
}: ItemEditorProps) => {
    useEffect(() => {
        if (moduleLayout !== ModuleLayout.VerticalEvaluation)
            ContentHandler.updateTitle(itemId, locale, '')
    }, [itemId, locale, moduleLayout])

    const setLocaleWiseMediaOpt = useCallback(
        (change: Change<Medias | undefined>) => {
            ItemEntityService.updateLocaleWiseMediaOpt(itemId, change)
        },
        [itemId]
    )

    const commonFieldProps = useMemo(
        () => ({
            workflowEntityId,
            itemId,
            locale,
            showValidationError,
            editDisabled,
            enabledOnAllLocales: true,
        }),
        [itemId, locale, showValidationError, editDisabled, workflowEntityId]
    )

    return (
        <>
            <ItemEditorValidationErrors {...{ itemId, showValidationError }} />
            <ItemEditorBasicInfo
                handler={ContentHandler}
                itemId={itemId}
                workflowEntityId={workflowEntityId}
                locale={locale}
                editDisabled={editDisabled}
                showValidationError={showValidationError}
            />
            {moduleLayout === ModuleLayout.VerticalEvaluation && (
                <>
                    <Spacer height={'S200'} />
                    <ResponsiveRow gridGap='S300' alignItems={'flex-start'}>
                        <Col width={'100%'}>
                            <ItemEntityField<ContentItemDTO, string>
                                {...commonFieldProps}
                                derive={(entity) =>
                                    entity.titleI18N ? entity.titleI18N[locale] ?? '' : ''
                                }
                            >
                                {({ value, props }) => (
                                    <ItemEditorTextInput
                                        {...props}
                                        inputId={'title'}
                                        value={value}
                                        placeholder={'An optional title for the item'}
                                        labelText={'Title'}
                                        setValue={(nextValue: string) => {
                                            ContentHandler.updateTitle(itemId, locale, nextValue)
                                        }}
                                    />
                                )}
                            </ItemEntityField>
                        </Col>
                    </ResponsiveRow>
                </>
            )}
            <Spacer height={'S200'} />
            <ResponsiveRow gridGap='S300' alignItems={'flex-start'} flexWrap={'wrap'}>
                <Col flex='1'>
                    <ItemEntityField<ContentItemDTO, string>
                        {...commonFieldProps}
                        derive={(entity) => entity.textI18N[locale] ?? ''}
                        deriveValidationErrorMessage={(errors) =>
                            (errors.validationErrors.textI18N ?? []).join(', ')
                        }
                    >
                        {({ value, props }) => (
                            <ItemEditorTextArea
                                {...props}
                                inputId={'text'}
                                dataTestId={'content-text-editor'}
                                value={value}
                                setValue={(nextValue: string) => {
                                    ContentHandler.updateText(itemId, locale, nextValue)
                                }}
                                labelText={'Text'}
                            />
                        )}
                    </ItemEntityField>
                </Col>
            </ResponsiveRow>
            <Spacer height='S400' />
            <Hr />
            <ItemEntityField<ContentItemDTO, Medias | undefined>
                {...commonFieldProps}
                derive={(entity) => entity.localeWiseMedia}
            >
                {({ value: localeWiseMediaOpt, props }) => (
                    <MediaManager
                        itemId={props.itemId}
                        locale={locale}
                        localeWiseMediaOpt={localeWiseMediaOpt}
                        setLocaleWiseMediaOpt={setLocaleWiseMediaOpt}
                        disabled={props.disabled}
                    />
                )}
            </ItemEntityField>
        </>
    )
}
